import React from 'react';

import {
  FormVariant,
  formVariantsOptions,
} from 'app/pages/loanApplication/tabs/tasks/Tasks.constants';
import { FormViewer } from 'app/pages/system/forms';
import CreateFormModal from 'app/shared/modules/CreateFormModal';
import _ from 'lodash';

import { CompanyType } from 'constants/company';
import { LoxDocumentTypeName } from 'constants/forms';
import { NATIONAL_CATEGORY_CODE } from 'constants/states';
import { DataType, LoanTaskFormStatus, TaskActionType } from 'constants/taskCategories';
import { TaskAssignmentType } from 'constants/tasks';
import { getConditionalObject } from 'utils/data';
import { getPriorToRequestFields } from 'utils/priorTo';
import { replaceValuesInsideCurlyBrackets } from 'utils/string';

import {
  NEW_FORM_DIALOG_HEADER,
  TASK_NAME_REPLACE_KEY,
  TASK_NAME_REPLACE_WITH_KEY,
} from './AddBrokerConditionForm.constants';

const buildFormParams = ({ name, formId, formType }) => ({
  name,
  formId,
  formType,
  selectedPage: 1,
});

export const applyUploadedFormToTask = ({ form, taskIndex, uploadedForm }) => {
  const newForm = _.cloneDeep(form);
  const formVariant = formVariantsOptions.find(
    variant => variant.value === FormVariant.LOAN_SPECIFIC,
  );

  newForm.taskTypeIds[taskIndex].formVariant = {
    ...formVariant,
    formId: uploadedForm.formId,
  };

  return newForm;
};

export const getFormViewerDialogProps = ({ uploadedForm, onAssignClick }) => ({
  header: NEW_FORM_DIALOG_HEADER,
  body: dialogProps => (
    <FormViewer
      isDisableResetStateAction
      isNeedCloseButton
      isShowAssignFormIconButton
      customOnAssignFormClick={onAssignClick}
      formParams={buildFormParams(uploadedForm)}
      {...dialogProps}
    />
  ),
  disableEnforceFocus: true,
});

export const getCreateFormDialogProps = ({
  abortController,
  applicationId,
  onFinishFormUploading,
}) => ({
  header: NEW_FORM_DIALOG_HEADER,
  body: dialogProps => (
    <CreateFormModal
      isDisableResetStateAction
      isLoanSpecificForm
      isPdfOnly
      abortController={abortController}
      applicationId={applicationId}
      defaultDocumentTypeName={LoxDocumentTypeName}
      formAreaCode={NATIONAL_CATEGORY_CODE}
      onFinishFormUploading={onFinishFormUploading}
      {...dialogProps}
    />
  ),
  afterCloseCallback: () => abortController.abort(),
});

const replaceTaskTypeDynamicFields = ({ string, options }) =>
  replaceValuesInsideCurlyBrackets({
    string,
    options,
    keyToReplace: TASK_NAME_REPLACE_KEY,
    keyToReplaceWith: TASK_NAME_REPLACE_WITH_KEY,
  });

const buildTaskAssignment = ({ taskType, loanId }) => {
  const isSlaGroup = taskType?.taskAssignmentType === TaskAssignmentType.SLA_GROUP;

  return {
    loanId,
    ...(isSlaGroup
      ? { type: 'SLA', slaGroupId: taskType.assignee?.value }
      : { type: 'Borrower', borrowerId: taskType.assignee?.value }),
  };
};

const buildTaskActionType = taskType => ({
  type: taskType.dataType,
  ...getConditionalObject(taskType?.dataType === DataType.FORMS, {
    formBundleId: taskType?.formId?.value,
    isSignatureRequired: taskType?.isSignatureRequired ?? false,
    formStatus: taskType?.formStatus ?? LoanTaskFormStatus.NEW,
    type: TaskActionType.FORM,
  }),
  ...getConditionalObject(taskType?.dataType === DataType.DOCUMENTS, {
    type: TaskActionType.DOCUMENT,
  }),
});

const buildBrokerTaskPayload = ({ taskType, loanId, taskTypeDynamicFieldOptions }) => ({
  taskTypeId: taskType.taskTypeId,
  taskAssignment: buildTaskAssignment({ taskType, loanId }),
  name: replaceTaskTypeDynamicFields({
    string: taskType.name,
    options: taskTypeDynamicFieldOptions,
  }),
  description: replaceTaskTypeDynamicFields({
    string: taskType.description,
    options: taskTypeDynamicFieldOptions,
  }),
  actionType: buildTaskActionType(taskType),
  relationId: taskType?.entity?.incomeId || taskType?.entity?.assetId || taskType.entity?.value,
});

export const buildConditionTasks = ({ form, loanId, taskTypeDynamicFieldOptions }) =>
  _.map(form.conditionTaskTypes || {}, taskType =>
    buildBrokerTaskPayload({ taskType, loanId, taskTypeDynamicFieldOptions }),
  );

export const prepareBrokerConditionPayload = ({
  form,
  isBrokerTasksEnabled,
  loanId,
  taskTypeDynamicFieldOptions,
}) => {
  const tasks = isBrokerTasksEnabled
    ? buildConditionTasks({ form, loanId, taskTypeDynamicFieldOptions })
    : [];

  return {
    type: CompanyType.BROKER,
    internalText: form.internalText?.toString(),
    borrowerInternalText: form.borrowerInternalText?.toString(),
    ...getPriorToRequestFields(form.priorToType),
    tasks,
  };
};

const prepareInternalConditionPayload = ({ form, loanId, taskTypeDynamicFieldOptions }) => ({
  type: CompanyType.INTERNAL,
  internalText: form.internalText?.toString(),
  borrowerInternalText: form.borrowerInternalText?.toString(),
  ...getPriorToRequestFields(form.priorToType),
  tasks: buildConditionTasks({ form, loanId, taskTypeDynamicFieldOptions }),
});

export const prepareInternalConditionRequest = ({
  form,
  applicationId,
  loanId,
  taskTypeDynamicFieldOptions,
}) => ({
  applicationId,
  slaGroupId: form.slaGroupId?.value,
  loanConditions: [prepareInternalConditionPayload({ form, loanId, taskTypeDynamicFieldOptions })],
});
